import { Component, computed, inject, signal, ElementRef, Renderer2 } from '@angular/core';
import { NgClass } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { filter, pipe } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { JwtPayload, UserInfo } from '../../model';
import { LayoutService, UserInfoService, UserService } from '../../services';
import { EnvironmentStore } from '../environment/environment.store';

@Component({
  selector: 'rb-profile',
  imports: [Button, NgClass, TranslatePipe, RouterLink],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
  host: {
    class:
      'hidden absolute top-[3.25rem] right-0 w-72 p-4 bg-surface-0 dark:bg-surface-900 border border-surface rounded-border origin-top shadow-[0px_3px_5px_rgba(0,0,0,0.02),0px_0px_2px_rgba(0,0,0,0.05),0px_1px_4px_rgba(0,0,0,0.08)]',
  },
})
export class ProfileComponent {
  layoutService: LayoutService = inject(LayoutService);
  readonly confirmationService = inject(ConfirmationService);
  readonly translateService = inject(TranslateService);
  readonly router = inject(Router);
  readonly userService = inject(UserService);
  readonly userInfoService = inject(UserInfoService);
  readonly appInfo = inject(EnvironmentStore);
  private readonly renderer = inject(Renderer2);
  private readonly el = inject(ElementRef);

  isAd = computed(() => this.appInfo.environment()?.isAd === true);

  userDetails = signal<UserInfo | undefined>(undefined);

  hideProfile() {
    this.renderer.addClass(this.el.nativeElement, 'hidden');
  }

  loadUserDetails = rxMethod<JwtPayload | undefined>(
    pipe(
      filter(jwt => jwt !== undefined),
      switchMap(() =>
        this.userService.getAuthenticatedUserDetails().pipe(
          tapResponse({
            next: user => this.userDetails.set(user),
            error: () => this.userDetails.set(undefined),
          })
        )
      )
    )
  );

  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  constructor() {
    this.loadUserDetails(this.userInfoService.jwt);
  }

  toggleDarkMode() {
    this.layoutService.appState.update(state => ({
      ...state,
      darkMode: !state.darkMode,
    }));
  }

  logout() {
    this.confirmationService.confirm({
      header: this.translateService.instant('topbar.menu.signOutTitle'),
      message: this.translateService.instant('topbar.menu.signOutMessage'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.router.navigateByUrl('logout');
      },
    });
  }
}
